import React from 'react';
import Content from '../components/content';
import Image from '../components/image';
import Video from '../components/video';
import Footer from '../components/footer';
import Layout from '../components/layout';
import CaseHeader from '../components/case-header';

const NewRelic = () => (
    <Layout>
        <CaseHeader
            title="New Relic"
            role="Product Design & systems"
            time="2019&ndash;2021"
            imgSrc={require('../assets/images/new-relic/header-3.gif')}
            alt="New Relic One Observability launch retro video"
            description="New Relic is a real-time observability platform that allows websites and apps to track software and hardware performance."
            quote="New Relic’s mission is to instrument the internet and help companies of all sizes and organizations around the world deliver more perfect software."
            quoteAuthor="Lew Cirne"
            quoteAuthorRole="Founder of New Relic"
        />
        <section>
            <Content kind="p" tag="h2">
                Intro
            </Content>
            <Content>
                In 2019, New Relic was building{' '}
                <a href="https://www.youtube.com/watch?v=pQweFFf6cew&amp;ab_channel=NewRelic">
                    New Relic One
                </a>
                , a new platform designed to move beyond traditional monitoring and embrace
                observability—giving teams a real-time view of all operational data in one place.
            </Content>
        </section>

        <section>
            <Content kind="p" tag="h2">
                Workloads
            </Content>
            <Content>
                My first challenge was leading the design efforts for a brand-new product,
                Workloads.
            </Content>
            <Content>
                New Relic One was built around the concept of{' '}
                <a href="https://docs.newrelic.com/docs/new-relic-one/use-new-relic-one/core-concepts/what-entity-new-relic/">
                    entities
                </a>
                . An entity can be any data-reporting component—such as an application, a database
                service, or a group of those items.{' '}
                <a href="https://docs.newrelic.com/docs/new-relic-one/use-new-relic-one/workloads/workloads-isolate-resolve-incidents-faster/">
                    Workloads
                </a>{' '}
                gives users the ability to group and monitor entities, providing aggregated health
                data across their entire system.
            </Content>
            <Content>
                The goal was to surface the unique insights a group can offer—such as the most
                frequent alerts, overall system health, grouping by type, and relationships with
                other entities. We worked in one-week sprints to deliver incremental value, starting
                by creating and aggregating entities, grouping metrics by type, and showing alerts
                by condition and priority.
            </Content>
            <Content>
                Our customers made it clear that we needed a quick way to identify critical issues
                and ensure the system’s overall health. We introduced an entity map that displays
                all entities and lets users group and order them by health status, and use it as a
                filter.
            </Content>
            <Image
                alt="Entity map explorations. Using a tree-map distribution and gradual color scheme."
                src={require('../assets/images/new-relic/workloads-entity-map-explorations.jpg')}
            />
            <Video
                alt="Entity map interactions to filter down the overview data."
                src={require('../assets/images/new-relic/workloads-entity-map.mp4')}
            />
            <Content>
                Entities surface different types of activity data that can affect workload
                status—such as alerts, configuration updates or changes, or reports from scriptable
                tools. For example, a service misconfiguration can cause problems in the main
                database. We built an Activity Stream visualization to surface all of that activity
                and its relationships.
            </Content>
            <Image
                alt="Early Activity Stream designs."
                src={require('../assets/images/new-relic/workloads-activity-stream.png')}
            />
            <Video
                alt="Activity Stream coded prototype to test density of information."
                src={require('../assets/images/new-relic/workloads-activity-stream.mp4')}
            />
            <Content>
                Iteration is key when building a product from the ground up. By shipping small
                increments each week, we could easily spot what was missing or needed improvement.
                After a year of development, we delivered a product that evolved from a simple table
                used by a dozen private beta users to a generally available offering for all New
                Relic customers.
            </Content>
            {/* <Image
                alt="Workloads with the entity map, breakdown status and metrics"
                src={require('../assets/images/new-relic/workloads-ga.jpg')}
            /> */}
        </section>
        <section>
            <Content kind="p" tag="h2">
                One Core Design System
            </Content>
            <Content>
                After Workloads, I joined One Core’s design systems team to set the product design
                direction and provide tools so product teams could deliver a consistent, coherent
                experience.
            </Content>
            <Content>
                Over two years, we developed, scaled, and maintained the design system by providing
                design foundations, a library of UI components for both designers and developers,
                and documented guidelines for common interaction patterns. We also supported product
                teams through direct guidance and by establishing review processes and feature
                request workflows.
            </Content>
            <Image
                alt="Documentation site with Foundations, Components usage and examples, API, Guides and more"
                src={require('../assets/images/new-relic/one-core-documentation.png')}
            />
            <Video
                alt="Step component for sequential, multi-step onboardings."
                src={require('../assets/images/new-relic/one-core-step-component.mp4')}
            />
            <Video
                alt="Tile component for grouping collection of related data."
                src={require('../assets/images/new-relic/one-core-tile-component.mp4')}
            />
        </section>
        <section>
            <Content kind="p" tag="h2">
                A11y
            </Content>
            <Content>
                In 2021, New Relic commissioned an external accessibility assessment, which found
                nearly a thousand accessibility issues at varying severity levels across the
                platform. These ranged from poor focus management and low-contrast text to a lack of
                screen reader support.
            </Content>
            <Content>
                We built an accessibility foundation at the core of the design system. It helps
                teams create accessible UIs and improve existing ones through dedicated components
                and documentation.
            </Content>
            <Content>
                I worked on revising the color palette to increase contrast between light and dark
                shades, expand the range of accessible color combinations, and make the middle
                shades more harmonious and perceptually uniform.
            </Content>
            <Content>
                I created the palette using a color appearance model (
                <a href="https://gramaz.io/d3-cam02/">CIECAM</a>) that models human color
                perception, and used contrast levels to define accessible color combinations (
                <a href="https://www.w3.org/WAI/WCAG2AA-Conformance">WCAG AA and AAA</a>
                ). These changes resolved over six hundred issues related to insufficient foreground
                and background contrast.
            </Content>
            <Image
                alt="Old vs new Color palette"
                src={require('../assets/images/new-relic/one-core-palette-comparison.gif')}
            />
            <Image
                alt="One Core accessibility and color usage documentation."
                src={require('../assets/images/new-relic/one-core-color-palette-documentation.png')}
            />
        </section>
        <Footer>
            <a href="./typeform">
                <img
                    loading="lazy"
                    src={require('../assets/images/typeform/header-3.gif')}
                    alt={'Typeform use case'}
                />
            </a>
            <a href="./playfulbet">
                <img
                    loading="lazy"
                    src={require('../assets/images/playfulbet/header.gif')}
                    alt={'Playfulbet use case'}
                />
            </a>
        </Footer>
    </Layout>
);

export default NewRelic;
